/**
 * Duplicate-scan debounce for the entrance kiosk (#2691).
 *
 * The camera decodes the same QR many times a second, and a player keeps
 * holding their phone up after the result shows. The gate accepts a code only
 * when no scan is in progress or on screen, and ignores the same code again
 * for `sameCodeCooldownMs` (longer than a pass lives, 40 s), so one wave never
 * fires twice and never turns a green into "Already used".
 */
export const SAME_CODE_COOLDOWN_MS = 45_000;

export interface ScanGate {
  /** True when this decoded code should be scanned now (and marks busy). */
  accept: (code: string, now: number) => boolean;
  /** The result has been shown; new codes may be scanned again. */
  release: () => void;
  isBusy: () => boolean;
  /** Accept this code again `afterMs` from `now` instead of after the full cooldown. */
  retryAfter: (code: string, now: number, afterMs: number) => void;
  /** Forget everything (re-pair, camera restart). */
  reset: () => void;
}

export function createScanGate({
  sameCodeCooldownMs = SAME_CODE_COOLDOWN_MS,
}: { sameCodeCooldownMs?: number } = {}): ScanGate {
  let busy = false;
  const lastAccepted = new Map<string, number>();

  return {
    accept(code, now) {
      if (busy || !code) return false;
      for (const [seen, at] of lastAccepted) {
        if (now - at >= sameCodeCooldownMs) lastAccepted.delete(seen);
      }
      if (lastAccepted.has(code)) return false;
      lastAccepted.set(code, now);
      busy = true;
      return true;
    },
    release() {
      busy = false;
    },
    isBusy() {
      return busy;
    },
    retryAfter(code, now, afterMs) {
      if (!lastAccepted.has(code)) return;
      lastAccepted.set(code, now - Math.max(0, sameCodeCooldownMs - afterMs));
    },
    reset() {
      busy = false;
      lastAccepted.clear();
    },
  };
}
